<script setup lang="ts">
/**
 * The marine bulletins: KNMI for the Dutch coastal waters and the North Sea, DWD for the
 * German Bight and the Baltic. The server fetches them from the sources every half hour
 * and this keeps whatever it last saw, so the forecast a skipper read in the harbour is
 * still readable where there is no signal — marked with the time it was issued, since a
 * stale forecast that says so is worth having and one that pretends to be current is not.
 */
import { ref, computed, onMounted } from 'vue'
import { useAppStore } from '../store'
import ScreenTitle from '../components/ScreenTitle.vue'
import { getMeteo, type Meteo } from '../api'
import { kvGet, kvSet } from '../db'

const store = useAppStore()
const bulletins = ref<Meteo | null>(null)
const area = ref<string | null>(null)
const stale = ref(false)

const shown = computed(() => bulletins.value?.areas.find(a => a.id === area.value) ?? bulletins.value?.areas[0] ?? null)
const fetched = computed(() => {
  const at = bulletins.value?.fetchedAt
  return at ? new Date(at).toLocaleString(store.settings.locale, { dateStyle: 'short', timeStyle: 'short' }) : null
})

onMounted(async () => {
  bulletins.value = (await kvGet<Meteo>('meteo')) ?? null
  area.value = bulletins.value?.areas[0]?.id ?? null
  try {
    const fresh = await getMeteo()
    bulletins.value = fresh
    if (!area.value) area.value = fresh.areas[0]?.id ?? null
    await kvSet('meteo', fresh)
  } catch {
    // no signal, or the server has none: what was kept is what there is
    stale.value = !!bulletins.value
  }
})
</script>

<template>
  <main class="screen">
    <div class="header">
      <ScreenTitle>{{ store.t('weatherTitle') }}</ScreenTitle>
      <select v-if="bulletins && bulletins.areas.length > 1" v-model="area" class="atlaspick">
        <option v-for="a in bulletins.areas" :key="a.id" :value="a.id">{{ a.name }}</option>
      </select>
    </div>

    <p v-if="stale" class="notice warn small">{{ store.t('weatherOffline') }}</p>

    <template v-if="shown">
      <p class="small muted">
        {{ store.t('source') }}: {{ shown.sources.join(', ') }}
        <template v-if="fetched"> · {{ store.t('weatherFetched') }} {{ fetched }}</template>
      </p>
      <!-- the server strips this markup to a handful of harmless tags before it stores it -->
      <article class="bulletin" v-html="shown.html" />
    </template>
    <p v-else class="notice">{{ store.t('weatherNone') }}</p>
  </main>
</template>
